/**
 * Reading a customer's item list straight out of the mail body.
 *
 * ---- why this exists ----
 *
 * There will be no server-side extraction endpoint. Whatever we can read, we read here, in the
 * browser, for nothing. Measured across 25 inbound mails on this bench (2026-08-12), **10 carry an
 * HTML table** and the real ones are labelled exactly like the spreadsheets:
 *
 *     Image      | Item Code | Description                         | Qty | UOM
 *     No image   | MSC0119   | Leather Gloves                      |   5 | Nos
 *     No image   | MSC0119   | Electrical Insulating Rubber Gloves |   2 | Nos
 *
 *     ML NO. | ITEM DESCRIPTIONS | REMARKS | QTY | UNIT | UP | Total Dhs.
 *
 * So a pasted requisition is the same problem as an attached one, and `sheetExtract.ts` already
 * solves it: find the header by LABEL, read the rows under it. This module's only job is turning
 * mail HTML into the rows that function expects.
 *
 * ---- every <tr> in document order, not table-by-table ----
 *
 * Mail HTML nests tables freely — Outlook wraps signatures, quoted replies and whole layouts in
 * them — and a non-greedy `<table>…</table>` regex breaks on the first nested one. So no attempt is
 * made to isolate tables: every row in the document is read in order, and `findHeaderRow` picks out
 * the one place a description column and a quantity column line up. Layout rows contribute noise
 * that carries no quantity, and the reader already skips those.
 *
 * Regex rather than `DOMParser`, matching `mail.ts`'s `htmlToText` — the test environment is
 * `node`, with no DOM, and this repo parses mail HTML this way already.
 */

import { readItemsFromPdfText } from './pdfItems'
import { readItemsDirectly, type SheetItem, type SheetRow } from './sheetExtract'
import { htmlToText } from './mail'

/** Entities seen in real mail. `&amp;` LAST, or `&amp;lt;` decodes all the way to `<`. */
function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, '&')
}

/** One cell's text: tags out, entities decoded, whitespace collapsed. */
function cellText(html: string): string {
  return decodeEntities(html.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim()
}

/**
 * Every table row in the mail, in document order.
 *
 * `<style>`/`<script>` go first: a mail's CSS contains braces and commas that would otherwise be
 * read as content, and one `<style>` block is bigger than most requisitions.
 */
export function htmlRows(html: string): SheetRow[] {
  const body = (html ?? '').replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
  const rows: SheetRow[] = []

  for (const match of body.matchAll(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi)) {
    const cells: string[] = []
    for (const cell of match[1].matchAll(/<(t[dh])\b[^>]*>([\s\S]*?)<\/\1>/gi)) {
      cells.push(cellText(cell[2]))
    }
    if (cells.length > 0) rows.push(cells)
  }

  return rows
}

/**
 * The items a customer pasted into the mail, or `null` when the body holds no readable table.
 *
 * `null` means "nothing here I can vouch for" — the caller falls back to the attachments, or tells
 * the purchaser to enter the lines. It never means "this mail has no items": a photographed list
 * or a sentence of prose reads as `null` too, and claiming zero would be worse than admitting we
 * cannot read it.
 */
export function readItemsFromMailBody(html: string): SheetItem[] | null {
  /*
    A TABLE first. When a customer pastes a requisition it is usually a real grid, and a grid
    carries its own column headings — which is far stronger evidence than anything a line of prose
    gives us.
  */
  const rows = htmlRows(html)
  if (rows.length > 0) {
    const fromTable = readItemsDirectly(rows)
    if (fromTable) return fromTable
  }

  /*
    A PIPE table next (2026-09-18) — the grid a markdown editor or a copied terminal produces,
    typed into a plain-text mail:

        | SL NO. | ITEM NAME / DESCRIPTION | QUANTITY | UNIT |
        | -----: | ----------------------- | -------: | ---- |
        |     21 | Battery CR2032          |       25 | pcs  |

    It carries its own headings, so it is the spreadsheet problem again, not the prose one — the
    same `readItemsDirectly` reads it once the lines are split on the pipes.
  */
  const piped = pipeRows(htmlToText(html))
  if (piped.length > 0) {
    const fromPipes = readItemsDirectly(piped)
    if (fromPipes) return fromPipes
  }

  /*
    Then the body as TEXT. Plenty of customers simply type the list:

        1. Printer INK (one each of each color) 4 pcs
        2. GMDSS VHF Radio SPARE BATTERY 3 pcs

    That is the same problem `pdfItems.ts` already solves — a line number, then a description, then
    a quantity beside a unit — so it is the same reader, not a second one written to look like it.
    Its guards come along too: a closed unit vocabulary, and the rejection of dates, phone numbers
    and reference numbers that would otherwise read as quantities.

    `htmlToText` is the SAME converter the reader pane uses, so what this parses is what the
    purchaser sees. Anything else and the two could disagree about what the mail even says.
  */
  return readItemsFromPdfText(htmlToText(html))
}

/**
 * The rows of a pipe table in plain text — lines with at least two `|`-separated cells. A
 * markdown separator line (`| ---: | --- |`) is dropped; a lone pipe in a sentence is not a
 * table and yields nothing (fewer than two rows). Cells are trimmed; an empty cell is `null`.
 */
export function pipeRows(text: string): SheetRow[] {
  const rows: SheetRow[] = []
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line.includes('|')) continue
    const cells = line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
    if (cells.length < 2) continue
    if (cells.every((c) => /^:?-{2,}:?$/.test(c) || c === '')) continue
    rows.push(cells.map((c) => (c === '' ? null : c)))
  }
  return rows.length >= 2 ? rows : []
}
